import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ArrowUpDown, ChevronDown, ChevronUp, Filter, X } from 'lucide-react';

// Toggle for the panel below. The arrow shows the action: down = expand, up = collapse.
// Must be rendered inside the same <Collapsible> as PositionsFilterPanel (which supplies
// aria-expanded / aria-controls).
export function PositionsFilterTrigger({ expanded, activeCount }) {
  const Arrow = expanded ? ChevronUp : ChevronDown;
  return (
    <CollapsibleTrigger asChild>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 text-sm"
        data-testid="pos-sort-filter-trigger"
      >
        Sort &amp; Filter
        {activeCount > 0 && (
          <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-xs" data-testid="pos-sort-filter-badge">
            {activeCount}
            <span className="sr-only"> active settings</span>
          </Badge>
        )}
        <Arrow className="h-4 w-4 ml-1.5" data-testid={expanded ? 'pos-sort-filter-chevron-up' : 'pos-sort-filter-chevron-down'} aria-hidden="true" />
      </Button>
    </CollapsibleTrigger>
  );
}

const STATUS_OPTIONS = [['open', 'Open / Unfilled'], ['filled', 'Filled'], ['cancelled', 'Canceled / Archived']];

// Sort + filter controls for the employer Positions tab (KAN-138). Rendered inline inside the
// positions control bar (a <Collapsible> root owned by the dashboard) and expanded/collapsed with a
// height animation by the "Sort & Filter" trigger there. Unmounted while collapsed. State lives in
// the dashboard so it survives collapse/expand; controls apply live as they change.
export function PositionsFilterPanel({
  positionsView,
  schoolTOCs,
  sortField,
  onSortFieldChange,
  sortDir,
  onSortDirChange,
  statusFilter,
  onStatusFilterChange,
  offeredTo,
  onOfferedToChange,
  filledBy,
  onFilledByChange,
  afterDate,
  onAfterDateChange,
  beforeDate,
  onBeforeDateChange,
  hasActiveFilters,
  onReset,
}) {
  const sortDisabled = positionsView !== 'list';

  return (
    // overflow-hidden is needed for the height animation; the -m/p pairs give focus rings of
    // controls at the edges room so they are not clipped. Spacing lives inside the animated element
    // so the animation has no margin jump. The test id is on the inner element, which only exists
    // while expanded (Radix leaves an empty hidden wrapper when collapsed).
    <CollapsibleContent
      className="-mx-1 -mb-1 overflow-hidden px-1 pb-1 data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up motion-reduce:animate-none"
    >
      <div className="mt-3 border-t border-slate-200 pt-3 space-y-3" data-testid="pos-sort-filter-panel">
      {/* Sort (list only) */}
      <div className="flex flex-wrap items-center gap-2">
        <ArrowUpDown className="h-4 w-4 text-slate-400" />
        <Select value={sortField} onValueChange={onSortFieldChange} disabled={sortDisabled}>
          <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="pos-sort-field">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="posted">Posted Date</SelectItem>
            <SelectItem value="position_date">Position Date</SelectItem>
            <SelectItem value="status">Status / Fulfillment</SelectItem>
            <SelectItem value="subject">Subject / Title</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortDir} onValueChange={onSortDirChange} disabled={sortDisabled}>
          <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="pos-sort-dir">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="asc">Ascending</SelectItem>
            <SelectItem value="desc">Descending</SelectItem>
          </SelectContent>
        </Select>
        {sortDisabled && <span className="text-xs text-slate-400">Sorting applies to the List view</span>}
      </div>

      {/* Filters (apply to both views) */}
      <div className="flex flex-wrap items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="h-9 text-sm" data-testid="pos-status-filter-trigger">
              <Filter className="h-3.5 w-3.5 mr-1.5" />
              {statusFilter.length === 0 ? 'Status: All' : `Status: ${statusFilter.length}`}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="start">
            {STATUS_OPTIONS.map(([val, label]) => (
              <label key={val} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                <Checkbox
                  checked={statusFilter.includes(val)}
                  onCheckedChange={(checked) => onStatusFilterChange(checked ? [...statusFilter, val] : statusFilter.filter((s) => s !== val))}
                  data-testid={`pos-status-${val}`}
                />
                <span>{label}</span>
              </label>
            ))}
          </PopoverContent>
        </Popover>

        <Select value={offeredTo} onValueChange={onOfferedToChange}>
          <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="pos-offered-to">
            <SelectValue placeholder="Offered to" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Offered to: Anyone</SelectItem>
            {schoolTOCs.map((t) => (
              <SelectItem key={t.user_id} value={t.user_id}>Offered to: {t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filledBy} onValueChange={onFilledByChange}>
          <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="pos-filled-by">
            <SelectValue placeholder="Filled by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Filled by: Anyone</SelectItem>
            {schoolTOCs.map((t) => (
              <SelectItem key={t.user_id} value={t.user_id}>Filled by: {t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* After/Before stay together on one line; on narrow screens the inputs share the width */}
        <div className="flex w-full flex-nowrap items-center gap-2 sm:w-auto" data-testid="pos-date-range">
          <div className="flex min-w-0 flex-1 items-center gap-1 text-sm sm:flex-none">
            <span className="text-slate-400">After</span>
            <Input type="date" value={afterDate} onChange={(e) => onAfterDateChange(e.target.value)} className="h-9 w-full min-w-0 text-sm sm:w-[150px]" data-testid="pos-after-date" />
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-1 text-sm sm:flex-none">
            <span className="text-slate-400">Before</span>
            <Input type="date" value={beforeDate} onChange={(e) => onBeforeDateChange(e.target.value)} className="h-9 w-full min-w-0 text-sm sm:w-[150px]" data-testid="pos-before-date" />
          </div>
        </div>

        {hasActiveFilters && (
          <Button type="button" variant="ghost" size="sm" className="h-9 text-sm text-slate-500" onClick={onReset} data-testid="pos-reset-filters">
            <X className="h-3.5 w-3.5 mr-1" /> Reset Filters
          </Button>
        )}
      </div>
      </div>
    </CollapsibleContent>
  );
}
